/**
 * Cart Addons Manager - Dawn Theme Compatible v4
 * 
 * Regole:
 * 1. Gli addon possono essere aggiunti solo se c'è un prodotto principale
 * 2. Se tutti i prodotti principali vengono rimossi, gli addon vengono rimossi automaticamente
 * 3. Le checkbox sono disabilitate se non c'è un prodotto principale
 * 4. Il checkout è disabilitato se non ci sono prodotti principali
 * 
 * v4: Aggiunto supporto per pagina /cart
 */
(() => {
  const ADDON_CHECKBOX_SELECTOR = '.addon-checkbox';
  const ADDON_PROP_KEY = '_addon';

  let isBusy = false;
  let updateQueue = null;

  // ============================================
  // DETECTION
  // ============================================

  function isCartPage() {
    return window.location.pathname === '/cart' || 
           window.location.pathname.startsWith('/cart/');
  }

  // ============================================
  // API HELPERS
  // ============================================

  async function fetchCart() {
    const res = await fetch('/cart.js', { 
      credentials: 'same-origin'
    });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  async function addAddonToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: { [ADDON_PROP_KEY]: 'true' }
        }]
      })
    });
    if (!res.ok) throw new Error('Failed to add addon');
    return res.json();
  }

  async function updateCartItems(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });
    if (!res.ok) throw new Error('Failed to update cart');
    return res.json();
  }

  // ============================================
  // CART ANALYSIS
  // ============================================

  function isAddonItem(item) {
    return item?.properties?.[ADDON_PROP_KEY] === 'true';
  }

  function getMainProducts(cart) {
    return cart.items.filter(item => !isAddonItem(item));
  }

  function getAddonItems(cart) {
    return cart.items.filter(isAddonItem);
  }

  function hasMainProduct(cart) {
    return getMainProducts(cart).length > 0;
  }

  function getRealItemCount(cart) {
    return getMainProducts(cart).reduce((sum, item) => sum + item.quantity, 0);
  }

  // ============================================
  // UI UPDATES
  // ============================================

  function syncCheckboxes(cart) {
    const checkboxes = document.querySelectorAll(ADDON_CHECKBOX_SELECTOR);
    if (!checkboxes.length) return;

    const hasMain = hasMainProduct(cart);
    const addonVariantIdsInCart = new Set(
      getAddonItems(cart).map(item => item.variant_id)
    );

    checkboxes.forEach(cb => {
      const variantId = Number(cb.dataset.addonVariantId);
      if (!variantId) return;

      cb.checked = addonVariantIdsInCart.has(variantId);
      cb.disabled = !hasMain || isBusy;
      
      // Aggiorna anche la classe sul label parent
      const label = cb.closest('.cart-addons__item');
      if (label) {
        label.classList.toggle('cart-addons__item--disabled', !hasMain);
        label.classList.toggle('cart-addons__item--checked', addonVariantIdsInCart.has(variantId));
      }
    });
  }

  function updateCheckoutButtons(cart) {
    const realItemCount = getRealItemCount(cart);
    
    // Il checkout dovrebbe essere disabilitato se:
    // - Non ci sono prodotti principali (solo addon o vuoto)
    const shouldDisable = realItemCount === 0;

    // Drawer checkout button
    const drawerBtn = document.querySelector('#CartDrawer-Checkout');
    if (drawerBtn) {
      drawerBtn.disabled = shouldDisable;
    }

    // Cart page checkout button
    document.querySelectorAll('[name="checkout"], .cart__checkout-button, #checkout').forEach(btn => {
      btn.disabled = shouldDisable;
    });
  }

  function updateCartTotals(cart) {
    // Aggiorna il totale visualizzato
    const totalsValue = document.querySelector('.totals__total-value');
    if (totalsValue) {
      totalsValue.textContent = formatMoney(cart.total_price);
    }
  }

  function formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `€${euros} EUR`;
  }

  // ============================================
  // CART PAGE REFRESH - Con flag per evitare loop
  // ============================================

  async function refreshCartPage(userTriggered = false) {
    // Sulla pagina cart:
    // - Se l'utente ha fatto un'azione (click checkbox/remove) → reload
    // - Se è un check automatico → NON reload (per evitare loop)
    
    if (userTriggered) {
      console.log('[CartAddons] User action detected, reloading cart page...');
      setTimeout(() => {
        window.location.reload();
      }, 100);
      return true;
    } else {
      console.log('[CartAddons] Automatic check on cart page, skipping reload');
      try {
        // Solo aggiorna UI senza reload
        const cart = await fetchCart();
        syncCheckboxes(cart);
        updateCheckoutButtons(cart);
        updateCartTotals(cart);
        return true;
      } catch (err) {
        console.error('[CartAddons] Error updating cart page UI:', err);
        return false;
      }
    }
  }

  // ============================================
  // DRAWER REFRESH
  // ============================================

  async function refreshDrawer() {
    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble', {
        credentials: 'same-origin'
      });
      
      if (!res.ok) {
        console.warn('[CartAddons] Failed to fetch sections');
        return false;
      }
      
      const sections = await res.json();
      
      // Salva lo stato del drawer
      const drawer = document.querySelector('cart-drawer');
      const wasOpen = drawer?.classList.contains('is-open') || 
                      drawer?.classList.contains('active') ||
                      drawer?.hasAttribute('open') ||
                      document.body.classList.contains('overflow-hidden');

      // Aggiorna cart-drawer
      if (sections['cart-drawer']) {
        const cartDrawerElement = document.querySelector('cart-drawer');
        if (cartDrawerElement) {
          const temp = document.createElement('div');
          temp.innerHTML = sections['cart-drawer'];
          const newDrawer = temp.querySelector('cart-drawer');
          
          if (newDrawer) {
            cartDrawerElement.innerHTML = newDrawer.innerHTML;
            cartDrawerElement.className = newDrawer.className;
            
            if (wasOpen) {
              cartDrawerElement.classList.add('active');
            }
          }
        }
      }

      // Aggiorna il bubble
      if (sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const temp = document.createElement('div');
          temp.innerHTML = sections['cart-icon-bubble'];
          const newContent = temp.querySelector('.shopify-section');
          if (newContent) {
            bubble.innerHTML = newContent.innerHTML;
          }
        }
      }

      // Riapri il drawer se necessario
      if (wasOpen) {
        const updatedDrawer = document.querySelector('cart-drawer');
        if (updatedDrawer) {
          if (typeof updatedDrawer.open === 'function') {
            requestAnimationFrame(() => updatedDrawer.open());
          } else {
            updatedDrawer.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }
        }
      }

      return true;
    } catch (err) {
      console.error('[CartAddons] Error refreshing drawer:', err);
      return false;
    }
  }

  // ============================================
  // SMART REFRESH - Sceglie drawer o page
  // ============================================

  async function refreshCart(userTriggered = false) {
    if (isCartPage()) {
      console.log('[CartAddons] Refreshing cart page...');
      return await refreshCartPage(userTriggered);
    } else {
      console.log('[CartAddons] Refreshing cart drawer...');
      return await refreshDrawer();
    }
  }

  // ============================================
  // CORE LOGIC
  // ============================================

  async function removeAllAddons(cart) {
    const addons = getAddonItems(cart);
    if (addons.length === 0) return cart;

    console.log('[CartAddons] Removing all addons - no main product in cart');
    
    const updates = {};
    addons.forEach(addon => {
      updates[addon.key] = 0;
    });

    return updateCartItems(updates);
  }

  async function enforceAddonRules(userTriggered = false) {
    if (isBusy) {
      updateQueue = true;
      return;
    }

    isBusy = true;
    console.log('[CartAddons] Enforcing addon rules...');

    try {
      let cart = await fetchCart();
      const hasMain = hasMainProduct(cart);
      const addons = getAddonItems(cart);
      const realCount = getRealItemCount(cart);

      console.log('[CartAddons] Cart state:', {
        hasMainProduct: hasMain,
        addonCount: addons.length,
        realItemCount: realCount,
        totalItems: cart.item_count,
        userTriggered: userTriggered
      });

      // Se non ci sono prodotti principali ma ci sono addon, rimuovili
      let addonsRemoved = false;
      if (!hasMain && addons.length > 0) {
        await removeAllAddons(cart);
        cart = await fetchCart();
        addonsRemoved = true;
      }

      // Refresh intelligente
      // - Se abbiamo rimosso addon per azione utente → reload
      // - Altrimenti solo sync UI
      if (addonsRemoved && userTriggered) {
        await refreshCart(true); // Reload perché abbiamo modificato il carrello
      } else {
        await refreshCart(false); // Solo sync UI
      }

      // Aggiorna UI
      syncCheckboxes(cart);
      updateCheckoutButtons(cart);
      updateCartTotals(cart);

    } catch (err) {
      console.error('[CartAddons] Error enforcing rules:', err);
    } finally {
      isBusy = false;

      if (updateQueue) {
        updateQueue = false;
        setTimeout(() => enforceAddonRules(userTriggered), 100);
      }
    }
  }

  async function handleAddonToggle(variantId, shouldAdd) {
    const cart = await fetchCart();
    
    if (shouldAdd && !hasMainProduct(cart)) {
      console.warn('[CartAddons] Cannot add addon without main product');
      return false;
    }

    const existingAddon = cart.items.find(
      item => item.variant_id === variantId && isAddonItem(item)
    );

    if (shouldAdd && !existingAddon) {
      await addAddonToCart(variantId);
    } else if (!shouldAdd && existingAddon) {
      await updateCartItems({ [existingAddon.key]: 0 });
    }

    return true;
  }

  // ============================================
  // EVENT HANDLERS
  // ============================================

  async function onCheckboxChange(e) {
    const checkbox = e.target.closest(ADDON_CHECKBOX_SELECTOR);
    if (!checkbox) return;

    const variantId = Number(checkbox.dataset.addonVariantId);
    if (!variantId) return;

    if (isBusy) {
      e.preventDefault();
      return;
    }

    isBusy = true;
    const shouldAdd = checkbox.checked;

    // Disabilita tutte le checkbox
    document.querySelectorAll(ADDON_CHECKBOX_SELECTOR).forEach(cb => {
      cb.disabled = true;
    });

    try {
      const success = await handleAddonToggle(variantId, shouldAdd);
      
      if (!success) {
        checkbox.checked = !shouldAdd;
      }
      
      // Refresh intelligente - AZIONE UTENTE
      await refreshCart(true); // ← userTriggered = true
      
      // Sincronizza stato
      const cart = await fetchCart();
      syncCheckboxes(cart);
      updateCheckoutButtons(cart);
      updateCartTotals(cart);

    } catch (err) {
      console.error('[CartAddons] Error toggling addon:', err);
      checkbox.checked = !shouldAdd;
      alert("Errore nell'aggiornamento del carrello. Riprova.");
    } finally {
      isBusy = false;
    }
  }

  // Debounce
  let cartInteractionTimeout = null;

  function scheduleRuleCheck() {
    if (cartInteractionTimeout) {
      clearTimeout(cartInteractionTimeout);
    }
    cartInteractionTimeout = setTimeout(() => {
      cartInteractionTimeout = null;
      enforceAddonRules(true); // ← userTriggered = true
    }, 600);
  }

  function onCartInteraction(e) {
    const target = e.target;

    // Ignora checkbox addon
    if (target.closest(ADDON_CHECKBOX_SELECTOR)) return;

    // Rileva interazioni carrello
    if (
      target.closest('cart-remove-button') ||
      target.closest('.cart-remove-button') ||
      target.closest('[data-cart-remove]') ||
      target.closest('quantity-input') ||
      target.closest('.quantity__button')
    ) {
      console.log('[CartAddons] Cart interaction detected');
      scheduleRuleCheck();
    }
  }

  function onQuantityChange(e) {
    if (e.target.matches('.quantity__input')) {
      if (e.target.closest('form[action*="/cart/add"]')) return;
      scheduleRuleCheck();
    }
  }

  // ============================================
  // PUBSUB INTEGRATION (Dawn theme)
  // ============================================

  function subscribeToDawnEvents() {
    // Dawn usa un sistema PubSub per gli eventi del carrello
    if (typeof window.subscribe === 'function') {
      window.subscribe('cart:updated', () => {
        console.log('[CartAddons] Dawn cart:updated event received');
        scheduleRuleCheck(); // Già passa true
      });
    }

    // Ascolta anche eventi custom
    document.addEventListener('cart:updated', scheduleRuleCheck);
    document.addEventListener('cart:refresh', scheduleRuleCheck);
  }

  // ============================================
  // MUTATION OBSERVER
  // ============================================

  function setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Quando il drawer viene aperto/aggiornato
        const drawer = document.querySelector('cart-drawer');
        if (drawer && (
          mutation.target === drawer ||
          mutation.target.closest?.('cart-drawer')
        )) {
          // Sincronizza le checkbox quando il drawer cambia (solo UI, no reload)
          fetchCart().then(cart => {
            syncCheckboxes(cart);
            updateCheckoutButtons(cart);
          }).catch(() => {});
          break;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'open']
    });
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function init() {
    console.log('[CartAddons] Initializing v4 (with cart page support)...');
    console.log('[CartAddons] Current page:', isCartPage() ? 'CART PAGE' : 'OTHER PAGE');

    // Event listeners
    document.addEventListener('change', onCheckboxChange);
    document.addEventListener('click', onCartInteraction);
    document.addEventListener('change', onQuantityChange);

    // Dawn events
    subscribeToDawnEvents();

    // Mutation observer
    setupMutationObserver();

    // Check iniziale (NON userTriggered per evitare reload all'init)
    enforceAddonRules(false);
  }

  // Avvia
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();